import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Swords, 
  Clock, 
  Hash, 
  Lock, 
  Globe, 
  Check, 
  Users,
  Info
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useBattle } from '../context/BattleContext';
import { BattleModeType } from '../types/battle';
import toast from 'react-hot-toast';

const CreateBattleRoom: React.FC = () => { // NOSONAR
  const navigate = useNavigate();
  const { createRoom } = useBattle();
  
  // Form state
  const [roomName, setRoomName] = useState('');
  const [mode, setMode] = useState<BattleModeType>('time');
  const [duration, setDuration] = useState(60);
  const [wordCount, setWordCount] = useState(50);
  const [includePunctuation, setIncludePunctuation] = useState(false);
  const [includeNumbers, setIncludeNumbers] = useState(false);
  const [isPublic, setIsPublic] = useState(true);
  const [maxParticipants, setMaxParticipants] = useState(10);
  
  // Validation state
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleCreateRoom = async () => {
    if (!roomName.trim()) {
      toast.error('Please enter a room name');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const roomSettings = {
        name: roomName.trim(),
        isPublic,
        settings: {
          mode,
          duration: mode === 'time' ? duration : undefined,
          wordCount: mode === 'words' ? wordCount : undefined,
          includePunctuation,
          includeNumbers,
          maxParticipants,
          isPublic
        },
        maxParticipants
      };
      
      // Call the createRoom function from BattleContext
      await createRoom(roomSettings);
      
      // Navigate to the room
      navigate('/battle/room');
      
    } catch (error) {
      console.error('Error creating room:', error);
      toast.error('Failed to create battle room');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto pb-20">
      {/* Header */}
      <div className="flex items-center mb-8">
        <button
          onClick={() => navigate('/battle')}
          className="mr-4 p-2 hover:bg-secondary/50 rounded-full transition-colors"
        >
          <ArrowLeft className="w-6 h-6 text-textMuted" />
        </button>
        <h1 className="text-3xl font-bold flex items-center">
          <Swords className="w-8 h-8 mr-3 text-accent" />
          Create Battle Room
        </h1>
      </div>
      
      {/* Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-cardBg rounded-xl p-8 shadow-lg border border-secondary mb-6"
      >
        <div className="space-y-6">
          {/* Room Name */}
          <div>
            <label htmlFor="room-name" className="block text-text font-medium mb-2">Room Name</label>
            <input
              id="room-name"
              type="text"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              placeholder="Enter a name for your battle room"
              className="w-full bg-secondary/30 border border-secondary rounded-lg p-3 focus:outline-none focus:border-accent text-text placeholder:text-textMuted/70"
              maxLength={32}
            />
          </div>
          
          {/* Battle Mode */}
          <div>
            <fieldset>
              <legend className="block text-text font-medium mb-2">Battle Mode</legend>
              <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setMode('time')}
                className={`flex items-center justify-center space-x-2 p-4 rounded-lg border ${
                  mode === 'time'
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-secondary bg-secondary/30 text-textMuted'
                } transition-colors`}
              >
                <Clock className="w-5 h-5" />
                <span className="font-medium">Time Mode</span>
              </button>
              <button
                onClick={() => setMode('words')}
                className={`flex items-center justify-center space-x-2 p-4 rounded-lg border ${
                  mode === 'words'
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-secondary bg-secondary/30 text-textMuted'
                } transition-colors`}
              >
                <Hash className="w-5 h-5" />
                <span className="font-medium">Word Mode</span>
              </button>
            </div>
            </fieldset>
            <div className="mt-2 text-xs text-textMuted flex items-center">
              <Info className="w-3 h-3 mr-1" />
              {mode === 'time' 
                ? 'Compete to see who can type the most words accurately within a set time limit'
                : 'Race to complete a specified number of words with the highest speed and accuracy'
              }
            </div>
          </div>
          
          {/* Time or Word Count */}
          <div>
            <label className="block text-text font-medium mb-2">
              {mode === 'time' ? 'Time Limit' : 'Word Count'}
            </label>
            {mode === 'time' ? (
              <div className="flex space-x-2">
                {[30, 60, 120, 180].map((seconds) => (
                  <button
                    key={seconds}
                    onClick={() => setDuration(seconds)}
                    className={`flex-1 py-3 px-2 rounded-lg border ${
                      duration === seconds
                        ? 'border-accent bg-accent/10 text-accent font-medium'
                        : 'border-secondary bg-secondary/30 text-textMuted'
                    } transition-colors`}
                  >
                    {seconds} sec
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex space-x-2">
                {[25, 50, 100, 200].map((words) => (
                  <button
                    key={words}
                    onClick={() => setWordCount(words)}
                    className={`flex-1 py-3 px-2 rounded-lg border ${
                      wordCount === words
                        ? 'border-accent bg-accent/10 text-accent font-medium'
                        : 'border-secondary bg-secondary/30 text-textMuted'
                    } transition-colors`}
                  >
                    {words} words
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Additional Options */}
          <fieldset>
            <legend className="block text-text font-medium mb-2">Additional Options</legend>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <div className={`w-5 h-5 rounded flex items-center justify-center ${
                  includePunctuation ? 'bg-accent' : 'bg-secondary/50'
                }`}>
                  {includePunctuation && <Check className="w-3 h-3 text-primary" />}
                </div>
                <input 
                  type="checkbox"
                  className="sr-only"
                  checked={includePunctuation}
                  onChange={() => setIncludePunctuation(!includePunctuation)}
                />
                <span className="text-textMuted">Include punctuation</span>
              </label>
              
              <label className="flex items-center space-x-2 cursor-pointer">
                <div className={`w-5 h-5 rounded flex items-center justify-center ${
                  includeNumbers ? 'bg-accent' : 'bg-secondary/50'
                }`}>
                  {includeNumbers && <Check className="w-3 h-3 text-primary" />}
                </div>
                <input 
                  type="checkbox"
                  className="sr-only"
                  checked={includeNumbers}
                  onChange={() => setIncludeNumbers(!includeNumbers)}
                />
                <span className="text-textMuted">Include numbers</span>
              </label>
            </div>
          </fieldset>
          
          {/* Room Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <fieldset>
              <legend className="block text-text font-medium mb-2">Room Type</legend>
              <div className="flex space-x-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                    isPublic ? 'bg-accent' : 'bg-secondary/50'
                  }`}>
                    {isPublic && <div className="w-2 h-2 bg-primary rounded-full"></div>}
                  </div>
                  <input 
                    type="radio"
                    className="sr-only"
                    checked={isPublic}
                    onChange={() => setIsPublic(true)}
                  />
                  <span className="text-text flex items-center">
                    <Globe className="w-4 h-4 mr-1 text-green-500" />
                    Public
                  </span>
                </label>
                
                <label className="flex items-center space-x-2 cursor-pointer">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                    isPublic ? 'bg-secondary/50' : 'bg-accent'
                  }`}>
                    {!isPublic && <div className="w-2 h-2 bg-primary rounded-full"></div>}
                  </div>
                  <input 
                    type="radio"
                    className="sr-only"
                    checked={!isPublic}
                    onChange={() => setIsPublic(false)}
                  />
                  <span className="text-text flex items-center">
                    <Lock className="w-4 h-4 mr-1 text-yellow-500" />
                    Private
                  </span>
                </label>
              </div>
              <p className="mt-2 text-xs text-textMuted flex items-center">
                <Info className="w-3 h-3 mr-1" />
                {isPublic 
                  ? 'Anyone can see and join this room' 
                  : 'Only people with the invite code can join'
                }
              </p>
            </fieldset>
            
            <fieldset>
              <legend className="block text-text font-medium mb-2">
                Max Participants {' '}
                <span className="text-textMuted ml-2 font-normal">(2-50)</span>
              </legend>
              <div className="flex items-center">
                <input
                  type="range"
                  min="2"
                  max="50"
                  value={maxParticipants}
                  onChange={(e) => setMaxParticipants(Number.parseInt(e.target.value, 10))}
                  className="flex-1 accent-accent"
                />
                <div className="ml-4 bg-secondary/30 px-3 py-1 rounded w-16 text-center font-medium">
                  {maxParticipants}
                </div>
              </div>
              <div className="mt-2 text-xs text-textMuted flex items-center">
                <Users className="w-3 h-3 mr-1" />
                {(() => {
                  if (maxParticipants > 30) return 'Large battle - expect more competition!';
                  if (maxParticipants > 10) return 'Medium-sized battle';
                  return 'Small, focused battle';
                })()}
              </div>
            </fieldset>
          </div>
        </div>
      </motion.div>
      
      {/* Summary & Create Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-cardBg rounded-xl p-6 shadow-lg border border-secondary mb-8"
      >
        <h2 className="text-lg font-bold mb-4">Battle Room Summary</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-secondary/20 p-3 rounded-lg">
            <div className="text-xs text-textMuted">Mode</div>
            <div className="font-medium flex items-center mt-1">
              {mode === 'time' ? (
                <>
                  <Clock className="w-4 h-4 mr-1 text-accent" />
                  {duration}s Time Mode
                </>
              ) : (
                <>
                  <Hash className="w-4 h-4 mr-1 text-accent" />
                  {wordCount} Words Mode
                </>
              )}
            </div>
          </div>
          <div className="bg-secondary/20 p-3 rounded-lg">
            <div className="text-xs text-textMuted">Room Type</div>
            <div className="font-medium flex items-center mt-1">
              {isPublic ? (
                <>
                  <Globe className="w-4 h-4 mr-1 text-green-500" />
                  Public Room
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-1 text-yellow-500" />
                  Private Room
                </>
              )}
            </div>
          </div>
          <div className="bg-secondary/20 p-3 rounded-lg">
            <div className="text-xs text-textMuted">Options</div>
            <div className="text-sm mt-1 text-textMuted">
              {includePunctuation ? 'Punctuation, ' : ''}
              {includeNumbers ? 'Numbers, ' : ''}
              {!includePunctuation && !includeNumbers && 'Standard text'}
              {(includePunctuation || includeNumbers) && 'enabled'}
            </div>
          </div>
          <div className="bg-secondary/20 p-3 rounded-lg">
            <div className="text-xs text-textMuted">Players</div>
            <div className="font-medium flex items-center mt-1">
              <Users className="w-4 h-4 mr-1 text-blue-400" />
              {maxParticipants} max participants
            </div>
          </div>
        </div>
        
        <div className="flex justify-end space-x-4">
          <button
            onClick={() => navigate('/battle')}
            className="px-6 py-3 bg-secondary hover:bg-secondary/70 text-text rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateRoom}
            disabled={isSubmitting || !roomName.trim()}
            className={`px-8 py-3 rounded-lg transition-colors flex items-center ${
              isSubmitting || !roomName.trim()
                ? 'bg-secondary/50 text-textMuted cursor-not-allowed'
                : 'bg-accent hover:bg-yellow-500 text-primary font-semibold'
            }`}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </>
            ) : (
              'Create Battle Room'
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default CreateBattleRoom;
